import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { enhancedApi } from '../services/api';

// Type guard to check if response has data property
const hasData = (response: any): response is { success: true; data: any; message: string } => {
  return response.success && 'data' in response;
};

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'superuser' | 'auditor' | 'reviewer';
  department_id: number | null;
  unit_id: number | null;
  department_name: string | null;
  unit_name: string | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await enhancedApi.auth.getCurrentUser();
      if (hasData(response)) {
        // Handle both mock and real API response structures
        const user = response.data.user || response.data;
        setUser(user);
      }
    } catch (error: any) {
      // Don't log 401 errors as they're expected for unauthenticated users
      if (error.response?.status !== 401) {
        console.error('Auth check failed:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (username: string, password: string) => {
    try {
      console.log('Attempting login with:', username);
      const response = await enhancedApi.auth.login(username, password);

      console.log('Login response:', response);

      if (hasData(response)) {
        console.log('Login successful, setting user:', response.data);
        // Handle both mock and real API response structures
        const user = response.data.user || response.data;
        setUser(user);
        return { success: true };
      } else {
        console.log('Login failed:', response.message);
        return { success: false, message: response.message };
      }
    } catch (error: any) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const logout = async () => {
    try {
      await enhancedApi.auth.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  const isAuthenticated = !!user;

  const hasRole = (role: string) => {
    return user?.role === role;
  };

  const hasAnyRole = (roles: string[]) => {
    return user ? roles.includes(user.role) : false;
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated,
    hasRole,
    hasAnyRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
